(function () {
  const DEFAULT_CONFIG = {
    localUrl: '',
    latestUrl: 'https://assets.poptex.com/meta/latest.json',
    cacheKey: 'poptex.content_metadata',
    reloadKey: 'poptex.content_metadata_reloaded_version',
    schemaMajor: 1,
    timeoutMs: 5000,
    syncTtlMs: 60000,
    autoRefreshOnRemoteUpdate: false,
    refreshDelayMs: 150
  };

  const state = {
    config: Object.assign({}, DEFAULT_CONFIG),
    snapshot: null,
    latest: null,
    source: '',
    status: 'idle',
    lastError: '',
    lastSyncAt: 0,
    initPromise: null,
    syncPromise: null,
    listeners: []
  };

  const clone = (value) => {
    if (value === undefined || value === null) return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_error) {
      return value;
    }
  };

  const majorVersion = (value) => {
    const major = Number(String(value || '').split('.')[0]);
    return Number.isFinite(major) ? major : 0;
  };

  const isValidSnapshot = (value) => {
    if (!value || typeof value !== 'object') return false;
    if (majorVersion(value.schemaVersion) !== state.config.schemaMajor) return false;
    return Array.isArray(value.scenes)
      && Array.isArray(value.events)
      && Array.isArray(value.templates)
      && Array.isArray(value.assets);
  };

  const hasSameContent = (first, second) => {
    if (!first || !second) return false;
    if (first.contentHash && second.contentHash) return first.contentHash === second.contentHash;
    return Boolean(first.contentVersion && first.contentVersion === second.contentVersion);
  };

  const isNewerOrSameSnapshot = (incoming, current) => {
    if (!current) return true;
    if (hasSameContent(incoming, current)) return true;
    const incomingUpdatedAt = Date.parse(incoming && (incoming.contentUpdatedAt || incoming.updatedAt) || '');
    const currentUpdatedAt = Date.parse(current && (current.contentUpdatedAt || current.updatedAt) || '');
    if (!Number.isFinite(incomingUpdatedAt)) return false;
    if (!Number.isFinite(currentUpdatedAt)) return true;
    return incomingUpdatedAt >= currentUpdatedAt;
  };

  const readCache = () => {
    try {
      const raw = window.localStorage.getItem(state.config.cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  };

  const writeCache = (snapshot) => {
    try {
      window.localStorage.setItem(state.config.cacheKey, JSON.stringify(snapshot));
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes or when quota is full.
    }
  };

  const notify = () => {
    state.listeners.slice().forEach((listener) => {
      try {
        listener(getSnapshot());
      } catch (error) {
        console.warn('POPTEX content registry listener failed:', error);
      }
    });
  };

  const dispatchUpdateEvent = (snapshot, source) => {
    try {
      window.dispatchEvent(new CustomEvent('poptex:metadata-updated', {
        detail: {
          source,
          contentVersion: snapshot && snapshot.contentVersion,
          contentHash: snapshot && snapshot.contentHash
        }
      }));
    } catch (_error) {
      // CustomEvent can be unavailable in very old browsers.
    }
  };

  const maybeRefreshPage = (snapshot, source, previousSnapshot) => {
    if (!state.config.autoRefreshOnRemoteUpdate || source !== 'r2' || !previousSnapshot) return;
    const version = snapshot && snapshot.contentVersion;
    if (!version) return;

    try {
      if (window.sessionStorage.getItem(state.config.reloadKey) === version) return;
      window.sessionStorage.setItem(state.config.reloadKey, version);
    } catch (_error) {
      // Session storage can be unavailable; still allow one best-effort refresh.
    }

    window.setTimeout(() => {
      window.location.reload();
    }, Number(state.config.refreshDelayMs) || 0);
  };

  const setStatus = (status, error) => {
    state.status = status;
    state.lastError = error ? String(error.message || error) : '';
  };

  const setSnapshot = (snapshot, source, persist) => {
    if (!isValidSnapshot(snapshot)) return false;
    if (hasSameContent(state.snapshot, snapshot)) return true;
    const previousSnapshot = state.snapshot;
    state.snapshot = snapshot;
    state.source = source || '';
    if (persist) writeCache(snapshot);
    notify();
    dispatchUpdateEvent(snapshot, source);
    maybeRefreshPage(snapshot, source, previousSnapshot);
    return true;
  };

  const fetchJson = async (url, timeoutMs) => {
    if (!url) return null;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs || state.config.timeoutMs);
    try {
      const response = await fetch(url, {
        credentials: 'omit',
        cache: 'no-cache',
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      return response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const resolveSnapshotUrl = (latest) => {
    if (!latest || typeof latest !== 'object') return '';
    if (latest.url) return latest.url;
    if (latest.contentVersion) {
      try {
        return new URL(`versions/${encodeURIComponent(latest.contentVersion)}.json`, state.config.latestUrl).toString();
      } catch (_error) {
        return '';
      }
    }
    return '';
  };

  const syncRemote = async (options) => {
    const syncOptions = options || {};
    if (!state.config.latestUrl) return getSnapshot();
    if (state.syncPromise) return state.syncPromise;
    const now = Date.now();
    const syncTtlMs = Number(state.config.syncTtlMs) || 0;
    if (!syncOptions.force && state.lastSyncAt && syncTtlMs > 0 && now - state.lastSyncAt < syncTtlMs) {
      return getSnapshot();
    }
    state.lastSyncAt = now;

    state.syncPromise = fetchJson(state.config.latestUrl, state.config.timeoutMs)
      .then((latest) => {
        state.latest = latest || null;
        if (!latest || !latest.contentVersion) return null;
        if (state.snapshot && latest.contentHash && state.snapshot.contentHash === latest.contentHash) return state.snapshot;
        if (state.snapshot && !latest.contentHash && latest.contentVersion === state.snapshot.contentVersion) return state.snapshot;
        if (majorVersion(latest.schemaVersion) !== state.config.schemaMajor) return null;
        return fetchJson(resolveSnapshotUrl(latest), state.config.timeoutMs);
      })
      .then((snapshot) => {
        if (snapshot && isNewerOrSameSnapshot(snapshot, state.snapshot)) setSnapshot(snapshot, 'r2', true);
        setStatus('ready', '');
        return getSnapshot();
      })
      .catch((error) => {
        console.warn('POPTEX metadata sync failed:', error);
        setStatus('remote-error', error);
        return getSnapshot();
      })
      .finally(() => {
        state.syncPromise = null;
      });

    return state.syncPromise;
  };

  const refreshRemote = (options) => {
    window.setTimeout(() => {
      syncRemote(options).catch(() => {});
    }, 0);
    return getSnapshot();
  };

  const init = (inputConfig) => {
    state.config = Object.assign({}, state.config, inputConfig || {});
    if (state.initPromise) return state.initPromise;

    const cached = readCache();
    if (isValidSnapshot(cached)) setSnapshot(cached, 'browser-cache', false);

    state.initPromise = fetchJson(state.config.localUrl, state.config.timeoutMs)
      .then((localSnapshot) => {
        if (localSnapshot && isNewerOrSameSnapshot(localSnapshot, state.snapshot)) {
          setSnapshot(localSnapshot, 'shopify-local', false);
        }
        setStatus('ready', '');
        window.setTimeout(syncRemote, 0);
        return getSnapshot();
      })
      .catch((error) => {
        console.warn('POPTEX local metadata load failed:', error);
        setStatus(state.snapshot ? 'ready' : 'local-error', error);
        window.setTimeout(syncRemote, 0);
        return getSnapshot();
      });

    return state.initPromise;
  };

  const getSnapshot = () => clone(state.snapshot);

  const byIdentity = (records, value) => {
    const key = String(value || '');
    return records.find((record) => record && [record.id, record.slug, record.handle].map(String).includes(key)) || null;
  };

  const list = (key) => {
    const snapshot = state.snapshot || {};
    return Array.isArray(snapshot[key]) ? clone(snapshot[key]) : [];
  };

  const listScenes = () => init().then(() => list('scenes'));
  const getScene = (id) => listScenes().then((records) => byIdentity(records, id));

  const listEvents = () => init().then(() => list('events'));
  const getEvent = (id) => listEvents().then((records) => byIdentity(records, id));

  const listSceneEvents = (sceneId) => Promise.all([getScene(sceneId), listEvents()])
    .then(([scene, events]) => {
      const ids = new Set([sceneId, scene && scene.id, scene && scene.slug].filter(Boolean).map(String));
      return events.filter((event) => ids.has(String(event.sceneId || '')) || ids.has(String(event.sceneSlug || '')));
    });

  const listTemplates = (query) => init().then(() => {
    const filters = query || {};
    return list('templates').filter((template) => {
      if (filters.eventId && String(template.eventId || '') !== String(filters.eventId)) return false;
      if (filters.sceneId && String(template.sceneId || '') !== String(filters.sceneId)) return false;
      return true;
    });
  });

  const getTemplate = (id) => listTemplates().then((records) => byIdentity(records, id));

  const listEventTemplates = (eventId) => Promise.all([getEvent(eventId), listTemplates()])
    .then(([event, templates]) => {
      const ids = new Set([eventId, event && event.id, event && event.slug].filter(Boolean).map(String));
      return templates.filter((template) => ids.has(String(template.eventId || '')) || ids.has(String(template.eventSlug || '')));
    });

  const listAssets = () => init().then(() => list('assets'));
  const getAsset = (id) => listAssets().then((records) => byIdentity(records, id));
  const listReferencePresets = (query) => init().then(() => {
    const filters = query || {};
    return list('referencePresets').filter((preset) => {
      if (filters.type && String(preset.type || '') !== String(filters.type)) return false;
      return true;
    });
  });

  const onChange = (listener) => {
    if (typeof listener !== 'function') return () => {};
    state.listeners.push(listener);
    return () => {
      state.listeners = state.listeners.filter((item) => item !== listener);
    };
  };

  window.PoptexContentRegistry = {
    init,
    sync: syncRemote,
    refresh: refreshRemote,
    getSnapshot,
    getLatest: () => clone(state.latest),
    getSource: () => state.source,
    getStatus: () => state.status,
    getLastError: () => state.lastError,
    hasSnapshot: () => Boolean(state.snapshot),
    onChange,
    listScenes,
    getScene,
    listEvents,
    getEvent,
    listSceneEvents,
    listTemplates,
    getTemplate,
    listEventTemplates,
    listAssets,
    getAsset,
    listReferencePresets
  };

  init(window.POPTEX_METADATA_CONFIG || {});
})();
